/**
 * LatestWeeklyReportCard — compact, read-only Overview-sidebar card showing the most recent
 * weekly report's key RAG status at a glance.
 *
 * Shows only: Schedule / Scope / Budget RAG dots, escalation status, week-ending date, and a
 * "View All" link that switches the project detail page to the "Weekly Reports" tab (where the
 * full form + history lives). The full WeeklyReportSection is intentionally NOT rendered on the
 * Overview tab — this card is the Overview surface for weekly status.
 *
 * RAG colours reuse the RiskDashboard palette (green → success, yellow → #c98a00, red → error)
 * so status semantics are consistent across the Overview dashboard. Colour is never the sole
 * carrier of meaning — every dot pairs with a text label.
 */
import { useSearchParams } from "react-router-dom";
import { TrendingUp } from "lucide-react";

import {
  submittedReportsOnly,
  useWeeklyReports,
} from "@/hooks/useWeeklyReports";
import type { EscalationStatus, RagStatus } from "@/types";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

// ─── RAG presentation ──────────────────────────────────────────────────────────
// Mirrors RiskDashboard LEVEL_CONFIG: green → --color-success, yellow → shared amber, red → --color-error.

const RAG_DOT: Record<RagStatus, string> = {
  green: "bg-success",
  yellow: "bg-[#c98a00]",
  red: "bg-error",
};

const RAG_LABEL: Record<RagStatus, string> = {
  green: "Green",
  yellow: "Yellow",
  red: "Red",
};

// Escalation is red/green only (no yellow) — matches WeeklyReportSection.
const ESCALATION_DOT: Record<EscalationStatus, string> = {
  green: "bg-success",
  red: "bg-error",
};

const ESCALATION_LABEL: Record<EscalationStatus, string> = {
  green: "Green",
  red: "Red",
};

function fmtDate(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value.length === 10 ? `${value}T00:00:00` : value);
  return Number.isNaN(d.getTime())
    ? String(value)
    : d.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
}

/** One RAG row — label left (muted), coloured dot + status word right. */
function RagRow({
  label,
  status,
}: {
  label: string;
  status: string | null;
}): JSX.Element {
  const dot = status ? RAG_DOT[status as RagStatus] : undefined;
  const word = status ? RAG_LABEL[status as RagStatus] : undefined;
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-xs text-text-secondary">{label}</span>
      {status && dot ? (
        <span className="inline-flex items-center gap-1.5 text-sm font-medium text-text-primary">
          <span
            className={cn("h-2 w-2 shrink-0 rounded-full", dot)}
            role="img"
            aria-label={`${label}: ${word}`}
          />
          {word}
        </span>
      ) : (
        <span
          className="text-sm text-text-secondary"
          aria-label={`${label}: not reported`}
        >
          —
        </span>
      )}
    </div>
  );
}

export interface LatestWeeklyReportCardProps {
  projectId: string;
}

/**
 * Compact latest-weekly-report status card for the Overview sidebar.
 * See docs/phase2/change-requests — weekly report Overview split.
 */
export function LatestWeeklyReportCard({
  projectId,
}: LatestWeeklyReportCardProps): JSX.Element {
  const [, setSearchParams] = useSearchParams();
  const { data: reports, isLoading, isError } = useWeeklyReports(projectId);
  // Drafts (V047) are unpublished — the Overview status is the latest SUBMITTED report only.
  const [latest] = submittedReportsOnly(reports);

  const viewAll = (): void => {
    setSearchParams({ tab: "weekly-reports" }, { replace: true });
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <h3 className="flex items-center gap-2 text-base font-semibold text-text-primary">
            <TrendingUp className="h-4 w-4 text-primary" aria-hidden="true" />
            Weekly Status
          </h3>
          <Button
            variant="link"
            size="sm"
            className="h-auto px-0"
            onClick={viewAll}
          >
            View All
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div
            className="space-y-2"
            role="status"
            aria-label="Loading weekly status"
          >
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
          </div>
        ) : isError ? (
          <p className="text-sm text-text-secondary">
            Failed to load weekly status.
          </p>
        ) : !latest ? (
          <p className="text-sm text-text-secondary">No weekly reports yet</p>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <span className="text-xs text-text-secondary">Week Ending</span>
              <span className="text-sm font-medium text-text-primary">
                {fmtDate(latest.week_ending)}
              </span>
            </div>

            <RagRow label="Schedule" status={latest.schedule} />
            <RagRow label="Scope" status={latest.scope} />
            <RagRow label="Budget" status={latest.budget_status} />

            {/* Escalation — red/green only */}
            <div className="flex items-center justify-between gap-3">
              <span className="text-xs text-text-secondary">Escalation</span>
              {latest.escalation ? (
                <span className="inline-flex items-center gap-1.5 text-sm font-medium text-text-primary">
                  <span
                    className={cn(
                      "h-2 w-2 shrink-0 rounded-full",
                      ESCALATION_DOT[latest.escalation as EscalationStatus],
                    )}
                    role="img"
                    aria-label={`Escalation: ${ESCALATION_LABEL[latest.escalation as EscalationStatus]}`}
                  />
                  {ESCALATION_LABEL[latest.escalation as EscalationStatus]}
                </span>
              ) : (
                <span
                  className="text-sm text-text-secondary"
                  aria-label="Escalation: not reported"
                >
                  —
                </span>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default LatestWeeklyReportCard;
